import { z } from 'zod';
import { idSchema } from './common.js';
import { teamTasksQuerySchema } from './notes-tasks.js';
/**
 * Meetings for the Calendar (Home, Team Tasks): Calendly calls a CRM user hosts. The worker's
 * `calendly-meetings` loop reads the Data team's query (`crm_calendly_meetings.sql`) from BigQuery
 * and keeps the result only in Redis under `MEETINGS_CACHE_KEY`; the API serves from there the
 * caller's own, or (Team Tasks) those of the people they reach.
 * A meeting is never a task, and the CRM keeps no copy in Postgres. Its invitees' emails are kept
 * only to find the client's project (the Meeting Companion's lookup), as the Data team's guide
 * allows, and are served to the meeting's host only.
 */
export const MEETINGS_CACHE_KEY = 'crm:meetings:calendly:v1';
/**
 * The one organisation the snapshot belongs to: the Data team's query reads Remote Leverage's
 * Calendly only, and the snapshot is one for all organisations, so no other may read it.
 */
export const MEETINGS_ORGANISATION_SLUG = 'remote-leverage';
/** After this long without a successful read the Calendar says its meetings may be out of date. */
export const MEETINGS_STALE_AFTER_MS = 15 * 60_000;
/**
 * The widest window `GET /meetings/mine|team` serves: the Calendar's month grid (6 weeks), with
 * room to spare. The worker keeps a meeting this long after it ended, so the month shows its past
 * days too (those it read while they were upcoming).
 */
export const MEETINGS_RANGE_MAX_MS = 45 * 24 * 60 * 60_000;
const instant = z.iso.datetime({ offset: true });
export const meetingSchema = z.object({
    /** Calendly's scheduled-event URI: stable, unique per host. */
    id: z.string(),
    title: z.string(),
    /** The query's `meeting_kind`: kickoff, va_interview, second_interview, va_onboarding, … */
    kind: z.string(),
    startAt: instant,
    endAt: instant,
    /** Zoom, Google Meet or Other. */
    platform: z.string(),
    /** The call link. A Zoom link carries the passcode: never logged. */
    joinUrl: z.string().nullable(),
    /** The invitees (Calendly gives their emails only). Personal data: never logged. */
    attendees: z
        .array(z.object({ name: z.string().nullable(), email: z.string() }))
        // A snapshot written before attendees were kept still reads.
        .default([]),
});
/** What the worker keeps in Redis: every meeting of every host, until 45 days after it ended. */
export const meetingsSnapshotSchema = z.object({
    /** When the last read from BigQuery succeeded. */
    refreshedAt: instant,
    /** The newest `updated_at` seen: the next read asks only for changes after it. */
    since: instant,
    /** When every upcoming meeting was last read whatever its `updated_at` (absent before the first). */
    fullReadAt: instant.optional(),
    meetings: z.array(meetingSchema.extend({ hostEmail: z.string() })),
});
/** A window of at most 45 days, `to` after `from`. */
function checkWindow({ from, to }, ctx) {
    const span = Date.parse(to) - Date.parse(from);
    if (span <= 0)
        ctx.addIssue({ code: 'custom', message: 'to must be after from', path: ['to'] });
    else if (span > MEETINGS_RANGE_MAX_MS)
        ctx.addIssue({ code: 'custom', message: 'at most 45 days', path: ['to'] });
}
/** `GET /meetings/mine?from=&to=`: a window of the caller's (a month grid), computed in their browser. */
export const myMeetingsQuerySchema = z
    .object({ from: instant, to: instant })
    .superRefine(checkWindow);
/**
 * `GET /meetings/team?from=&to=&teamIds=&assigneeIds=`: Team Tasks' Calendar, for the people the
 * caller's `task.read` reaches, narrowed like `GET /tasks/team`.
 */
export const teamMeetingsQuerySchema = z
    .object({
    from: instant,
    to: instant,
    teamIds: teamTasksQuerySchema.shape.teamIds,
    assigneeIds: teamTasksQuerySchema.shape.assigneeIds,
})
    .superRefine(checkWindow);
export const myMeetingsSchema = z.object({
    /** When the meetings were last read from BigQuery; null when nothing has been read yet. */
    refreshedAt: instant.nullable(),
    /** No successful read for `MEETINGS_STALE_AFTER_MS`. */
    stale: z.boolean(),
    /**
     * The first start and last end among every meeting the caller may see, whatever the window:
     * the months the Calendar can offer. Null when there are none.
     */
    span: z.object({ first: instant, last: instant }).nullable(),
    items: z.array(meetingSchema),
});
/**
 * A meeting on Team Tasks, with its host. Someone else's meeting comes without its join link and
 * invitees (`joinUrl` null, `attendees` empty): those stay with the host.
 */
export const teamMeetingsSchema = myMeetingsSchema.extend({
    items: z.array(meetingSchema.extend({ host: z.object({ id: idSchema, name: z.string() }) })),
});
//# sourceMappingURL=meetings.js.map